import type { AdminServiceStatus } from "@/gen/nodebase/v1/admin_pb";

export type Health = "healthy" | "degraded" | "stale" | "unknown" | "stopped";
export const severity: Record<Health, number> = { degraded: 0, stale: 1, unknown: 2, healthy: 3, stopped: 4 };
export const businessGroups = [
  { key: "all", label: "All services" },
  { key: "control", label: "Control plane" },
  { key: "workers", label: "Workers" },
  { key: "network", label: "Networking" },
  { key: "storage", label: "Backup & storage" },
  { key: "sites", label: "Sites & delivery" },
  { key: "infra", label: "Infrastructure" },
] as const;
export type BusinessGroup = typeof businessGroups[number]["key"];
const families: Record<string, { name: string; business: BusinessGroup; description: string }> = {
  control: { name: "Control API", business: "control", description: "Account and Worker orchestration" },
  database: { name: "PostgreSQL", business: "control", description: "Control data and database process" },
  worker: { name: "Worker fleet", business: "workers", description: "Agent health across active Worker VMs" },
  transport: { name: "Host Transport", business: "network", description: "Regional transport processes and listener health" },
  edge: { name: "Edge network", business: "network", description: "Device relay and network exit processes and heartbeats" },
  ingress: { name: "Public Web gateways", business: "sites", description: "Website HTTP/HTTPS routing, ingress processes and heartbeats" },
  web: { name: "Public websites", business: "sites", description: "Website and Dashboard web entry" },
  download: { name: "Downloads", business: "sites", description: "Download origin and regional delivery mirror" },
  backup: { name: "Backup & recovery", business: "storage", description: "Backup reports, schedules and restore drills" },
  "vm-hosts": { name: "VM hosts", business: "infra", description: "Hypervisors, storage unlock, firewall and network reconciliation" },
  dns: { name: "DNS resolution", business: "network", description: "Regional resolver health checks and schedules" },
  certificate: { name: "DNS & certificates", business: "infra", description: "Managed on bs · live DNS/TLS checks · expiry warning below 24 days" },
  monitoring: { name: "Service monitoring", business: "infra", description: "Scheduled collection of platform health evidence" },
  test: { name: "Control test environment", business: "control", description: "Isolated Control test process" },
};
const places: Record<string, { key: string; label: string; note: string }> = {
  "nb-1": { key: "jp", label: "Tokyo", note: "nb-1 · Japan" }, jp: { key: "jp", label: "Tokyo", note: "nb-1 · Japan" },
  "nb-2": { key: "us", label: "United States", note: "nb-2 · US" }, us: { key: "us", label: "United States", note: "nb-2 · US" },
  "nb-hk": { key: "hk", label: "Hong Kong", note: "nb-hk · HK" }, hk: { key: "hk", label: "Hong Kong", note: "nb-hk · HK" },
  external: { key: "external", label: "External", note: "Independent provider evidence" },
};
export function location(row: Pick<AdminServiceStatus, "region">) {
  return places[row.region] ?? { key: row.region || "control", label: row.region || "Control plane", note: "Control records & application evidence" };
}
export function health(row: { status: string }, expired: boolean): Health {
  if (row.status === "stopped") return "stopped";
  return expired ? "stale" : Object.hasOwn(severity, row.status) ? row.status as Health : "unknown";
}
export function counts(rows: readonly { status: string }[], expired: boolean) {
  const result: Record<Health, number> = { healthy: 0, degraded: 0, stale: 0, unknown: 0, stopped: 0 };
  for (const row of rows) result[health(row, expired)]++;
  return result;
}
export type ServiceGroup = { id: string; name: string; business: BusinessGroup; description: string; status: Health; checks: AdminServiceStatus[] };
export function groupServices(rows: AdminServiceStatus[], expired: boolean): ServiceGroup[] {
  const groups = new Map<string, Omit<ServiceGroup, "status">>();
  for (const row of rows) {
    const family = row.id.endsWith("/edge-portmap") ? "edge" : ["host", "firewall", "network", "storage"].includes(row.kind) ? "vm-hosts" : row.kind;
    const definition = families[family];
    // Unrecognized services stay individually visible instead of disappearing
    // into a catch-all healthy category. External providers are distinct owners.
    const id = (definition ? family : row.id) + (row.status === "stopped" ? "/stopped" : "");
    if (!groups.has(id)) groups.set(id, { id, name: definition?.name ?? row.name,
      business: definition?.business ?? (row.id === "external/r2" ? "storage" : row.id === "external/dns-cdn" ? "sites" : "infra"),
      description: definition?.description ?? (row.id === "external/r2" ? "Recent R2 backup results · freshness and failures" : "Independent dependency evidence"), checks: [] });
    groups.get(id)!.checks.push(row);
  }
  return [...groups.values()].map((group) => {
    const checks = [...group.checks].sort((a, b) => severity[health(a, expired)] - severity[health(b, expired)] || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
    return { ...group, checks, status: health(checks[0], expired) };
  }).sort((a, b) => severity[a.status] - severity[b.status] || a.name.localeCompare(b.name));
}
